import { useEffect, useRef, useCallback } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * useEventStream — subscribe to backend Server-Sent Events for the current user.
 *
 * Usage:
 *   useEventStream({
 *     token,                // JWT from AuthContext
 *     enabled: !!token,
 *     onEvent: (type, data) => { ... },   // generic handler
 *     handlers: {
 *       order_status_changed: (data) => { ... },
 *       connected: (data) => { ... },
 *     },
 *   });
 *
 * The connection auto-reconnects on error with an exponential backoff
 * capped at 30s.
 */
export const useEventStream = ({ token, enabled = true, onEvent, handlers = {} } = {}) => {
  const sourceRef = useRef(null);
  const retryRef = useRef(0);
  const timeoutRef = useRef(null);
  const handlersRef = useRef(handlers);
  const onEventRef = useRef(onEvent);

  // Keep the latest handlers in a ref so we don't reconnect on every render.
  useEffect(() => {
    handlersRef.current = handlers;
    onEventRef.current = onEvent;
  }, [handlers, onEvent]);

  const cleanup = useCallback(() => {
    if (sourceRef.current) {
      try { sourceRef.current.close(); } catch { /* noop */ }
      sourceRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!enabled || !token || typeof window === 'undefined' || !window.EventSource) {
      return undefined;
    }

    const connect = () => {
      cleanup();
      const url = `${BACKEND_URL}/api/events/stream?token=${encodeURIComponent(token)}`;
      const es = new window.EventSource(url);
      sourceRef.current = es;

      const dispatch = (type, ev) => {
        let data = null;
        try { data = ev.data ? JSON.parse(ev.data) : null; } catch { data = ev.data; }
        try { onEventRef.current?.(type, data); } catch { /* noop */ }
        try { handlersRef.current?.[type]?.(data); } catch { /* noop */ }
      };

      // Auto-subscribe to any event name provided by the caller in `handlers`,
      // plus a few well-known lifecycle events. This prevents silent drops when
      // new SSE event types are introduced on the backend.
      const KNOWN_LIFECYCLE = ['connected', 'error', 'message'];
      const eventNames = new Set([
        ...KNOWN_LIFECYCLE,
        ...Object.keys(handlersRef.current || {}),
      ]);
      eventNames.forEach((name) => es.addEventListener(name, (ev) => dispatch(name, ev)));

      // Generic fallback for unnamed events
      es.onmessage = (ev) => dispatch('message', ev);

      es.onopen = () => { retryRef.current = 0; };
      es.onerror = () => {
        // Reconnect with backoff
        cleanup();
        const delay = Math.min(30000, 1000 * Math.pow(2, retryRef.current));
        retryRef.current += 1;
        timeoutRef.current = setTimeout(connect, delay);
      };
    };

    connect();
    return cleanup;
  }, [token, enabled, cleanup]);
};

export default useEventStream;
